import { useEffect, useMemo, useRef, useState } from 'react';
import {
  answerText,
  choiceResults,
  CHOICE_TYPES,
  formatSeconds,
  isEmptyAnswer,
  OTHER_PREFIX,
  responsesOf,
  validateFormAnswers,
  type FormAnswer,
  type FormAnswers,
  type FormField,
  type Message,
} from '@machs/shared';
import { useSync } from '../lib/sync';
import { usePlan } from '../lib/plan';
import { Icon } from '../lib/icons';
import { imageUrl } from '../lib/images';
import { mediaUrl } from '../lib/api';
import { localMediaUrl } from '../lib/storage';
import { useToast } from './ui';

// Fragebogen einer Rückfrage im Chat: beantworten wie eine WhatsApp-Umfrage,
// Ergebnisse für alle sichtbar, bis die Rückfrage abgeschlossen wird.

export function FormCard({ m }: { m: Message }) {
  const { client, me, version } = useSync();
  const plan = usePlan();
  const toast = useToast();
  const form = m.form!;
  const responses = useMemo(() => responsesOf(client, m.id), [client, version, m.id]);
  const own = responses.find((r) => r.userId === me.user.id);
  const [editing, setEditing] = useState(!own);
  const [draft, setDraft] = useState<FormAnswers>(own?.answers ?? {});
  const [other, setOther] = useState<Record<string, string>>({});
  const [voice, setVoice] = useState<string | null>(own?.voiceMediaId ?? null);
  const [busy, setBusy] = useState(false);
  const open = m.questionOpen;
  const canAnswer = open && (!me.guest || me.guest.canWrite);
  const input = editing && canAnswer;
  const showResults = !form.hideResults || !!own || m.authorId === me.user.id;
  const quick = form.fields.length === 1 && form.fields[0]!.type === 'radio' && !form.fields[0]!.allowOther && !form.allowVoice;

  useEffect(() => {
    if (own && !editing) setDraft(own.answers);
  }, [own?.updatedAt]);

  const set = (id: string, v: FormAnswer) => setDraft((d) => ({ ...d, [id]: v }));

  function withOther(): FormAnswers {
    const out: FormAnswers = { ...draft };
    for (const f of form.fields) {
      const text = other[f.id]?.trim();
      if (!f.allowOther || !text) continue;
      const v = out[f.id];
      const tagged = `${OTHER_PREFIX}${text}`;
      if (f.type === 'checkbox') out[f.id] = [...(Array.isArray(v) ? v.filter((x) => !x.startsWith(OTHER_PREFIX)) : []), tagged];
      else if (v === OTHER_PREFIX || isEmptyAnswer(v)) out[f.id] = tagged;
    }
    for (const [k, v] of Object.entries(out)) if (v === OTHER_PREFIX) delete out[k];
    return out;
  }

  async function submit(answers = withOther()) {
    const problem = validateFormAnswers(form, answers, !!voice);
    if (problem) return toast(problem);
    setBusy(true);
    try {
      await client.answerForm(m.id, answers, voice);
      setEditing(false);
    } finally {
      setBusy(false);
    }
  }

  async function upload(field: FormField, files: FileList | null) {
    if (!files?.length) return;
    if (!plan.allowUpload([...files].slice(0, 10).reduce((n, f) => n + f.size, 0))) return;
    const ids: string[] = [];
    for (const file of [...files].slice(0, 10)) {
      ids.push(
        await client.queueFile({
          kind: file.type.startsWith('image/') ? 'bild' : file.type.startsWith('video/') ? 'video' : 'datei',
          file,
          fileName: file.name,
          mime: file.type || null,
          durationSec: null,
          width: null,
          height: null,
        }),
      );
    }
    const prev = draft[field.id];
    set(field.id, [...(Array.isArray(prev) ? prev : []), ...ids]);
  }

  return (
    <div className="form-card">
      {form.fields.map((f) => {
        const choice = CHOICE_TYPES.includes(f.type);
        const { results, total } = choice ? choiceResults(f, responses) : { results: [], total: 0 };
        const texts = !choice ? responses.filter((r) => !isEmptyAnswer(r.answers[f.id])) : [];
        const value = draft[f.id];
        return (
          <div key={f.id} className="form-field">
            <div className="form-label">
              {f.label}
              {f.required && <span className="text-[var(--problem)]"> *</span>}
            </div>
            {f.help && <div className="form-help">{f.help}</div>}

            {choice && (
              <div className="flex flex-col gap-1.5">
                {results.filter((r) => !r.other).map((r) => {
                  const picked = f.type === 'checkbox' ? Array.isArray(value) && value.includes(r.option) : value === r.option;
                  const pct = total ? Math.round((r.count / total) * 100) : 0;
                  return (
                    <button
                      key={r.option}
                      className={`poll-option ${picked ? 'on' : ''}`}
                      disabled={!input}
                      onClick={() => {
                        if (f.type === 'checkbox') {
                          const list = Array.isArray(value) ? value : [];
                          set(f.id, picked ? list.filter((x) => x !== r.option) : [...list, r.option]);
                        } else if (quick) {
                          void submit({ [f.id]: r.option });
                        } else {
                          set(f.id, picked ? null : r.option);
                        }
                      }}
                      aria-pressed={picked}
                    >
                      {showResults && <span className="poll-bar" style={{ width: `${pct}%` }} />}
                      <span className={`poll-mark ${f.type === 'checkbox' ? 'box' : ''}`}>{picked && <Icon name="check" size={12} strokeWidth={3} />}</span>
                      <span className="relative flex-1 text-left">{r.option}</span>
                      {showResults && r.count > 0 && (
                        <span className="relative text-[12px] text-[var(--muted)]" data-tip={r.names.join(', ')} data-tip-wrap="">
                          {r.count}
                        </span>
                      )}
                    </button>
                  );
                })}
                {f.allowOther && input && (
                  <input
                    className="input h-9 text-[14px]"
                    placeholder="Eigene Antwort"
                    value={other[f.id] ?? (typeof value === 'string' && value.startsWith(OTHER_PREFIX) ? value.slice(OTHER_PREFIX.length) : '')}
                    onChange={(e) => {
                      setOther((o) => ({ ...o, [f.id]: e.target.value }));
                      if (f.type !== 'checkbox') set(f.id, e.target.value ? OTHER_PREFIX : null);
                    }}
                  />
                )}
                {showResults && results.some((r) => r.other) && (
                  <div className="text-[12.5px] text-[var(--muted)]">
                    Eigene Antworten: {results.filter((r) => r.other).map((r) => `${r.option} (${r.names[0]})`).join(', ')}
                  </div>
                )}
                {showResults && total > 0 && (
                  <div className="text-[12px] text-[var(--muted)]">{results.filter((r) => !r.other && r.count).map((r) => `${r.option}: ${r.names.join(', ')}`).join(' · ')}</div>
                )}
              </div>
            )}

            {input && f.type === 'text' && <input className="input h-10 text-[14.5px]" value={(value as string) ?? ''} onChange={(e) => set(f.id, e.target.value)} />}
            {input && f.type === 'textarea' && <textarea className="input min-h-[70px] py-2 text-[14.5px]" value={(value as string) ?? ''} onChange={(e) => set(f.id, e.target.value)} />}
            {input && f.type === 'number' && (
              <input className="input h-10 w-40 text-[14.5px]" type="number" inputMode="decimal" value={typeof value === 'number' ? value : ''} onChange={(e) => set(f.id, e.target.value === '' ? null : Number(e.target.value))} />
            )}
            {input && f.type === 'date' && <input className="input h-10 w-48 text-[14.5px]" type="date" value={(value as string) ?? ''} onChange={(e) => set(f.id, e.target.value || null)} />}
            {input && f.type === 'file' && (
              <div className="flex flex-wrap items-center gap-2">
                {(Array.isArray(value) ? value : []).map((id) => <FileThumb key={id} id={id} onRemove={() => set(f.id, (value as string[]).filter((x) => x !== id))} />)}
                <label className="chip cursor-pointer">
                  <Icon name="clip" size={14} />Datei wählen
                  <input type="file" hidden multiple onChange={(e) => (void upload(f, e.target.files), (e.target.value = ''))} />
                </label>
              </div>
            )}

            {!choice && showResults && texts.length > 0 && (
              <ul className="form-answers">
                {texts.map((r) => (
                  <li key={r.id}>
                    <b>{r.userName}{r.authorKind === 'guest' ? ' (extern)' : ''}:</b>{' '}
                    {f.type === 'file'
                      ? <span className="inline-flex flex-wrap gap-1.5 align-middle">{(r.answers[f.id] as string[]).map((id) => <FileThumb key={id} id={id} />)}</span>
                      : answerText(f, r.answers[f.id])}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}

      {showResults && responses.some((r) => r.voiceMediaId) && (
        <div className="form-field">
          <div className="form-label">Sprachantworten</div>
          {responses.filter((r) => r.voiceMediaId).map((r) => <VoiceAnswer key={r.id} name={r.userName} mediaId={r.voiceMediaId!} />)}
        </div>
      )}

      <div className="form-foot">
        {input && !quick && (
          <>
            <button className="btn btn-primary h-9 px-4" disabled={busy} onClick={() => void submit()}>{own ? 'Antwort ändern' : 'Antwort senden'}</button>
            {form.allowVoice && <VoiceRecorder mediaId={voice} onChange={setVoice} />}
            {own && <button className="btn btn-ghost h-9 px-3" onClick={() => (setEditing(false), setDraft(own.answers))}>Abbrechen</button>}
          </>
        )}
        {!input && own && canAnswer && <button className="btn btn-ghost h-9 px-3" onClick={() => setEditing(true)}>Antwort ändern</button>}
        <span className="ml-auto text-[12px] text-[var(--muted)]">
          {responses.length} {responses.length === 1 ? 'Antwort' : 'Antworten'}
          {!open && ', abgeschlossen'}
        </span>
        {!me.guest && (
          <button className="text-[12px] font-semibold text-[var(--brand-a)]" onClick={() => void client.updateMessage(m.id, { questionOpen: !open })}>
            {open ? 'Abschließen' : 'Wieder öffnen'}
          </button>
        )}
      </div>
    </div>
  );
}

function FileThumb({ id, onRemove }: { id: string; onRemove?: () => void }) {
  const { client } = useSync();
  const media = client.data.media.get(id);
  const src = localMediaUrl(id) ?? mediaUrl(id);
  return (
    <span className="relative inline-flex">
      {media?.kind === 'bild' ? (
        <a href={src} target="_blank" rel="noreferrer"><img src={imageUrl(id) ?? src} alt="" className="h-14 w-14 rounded-lg object-cover" /></a>
      ) : (
        <a href={src} target="_blank" rel="noreferrer" className="chip"><Icon name="file" size={14} />{media?.fileName ?? 'Datei'}</a>
      )}
      {onRemove && (
        <button className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full bg-[var(--text)] text-[var(--bg)]" onClick={onRemove} aria-label="Entfernen">
          <Icon name="x" size={11} />
        </button>
      )}
    </span>
  );
}

function VoiceAnswer({ name, mediaId }: { name: string; mediaId: string }) {
  const { client, version } = useSync();
  const media = useMemo(() => client.data.media.get(mediaId), [client, version, mediaId]);
  return (
    <div className="mt-1">
      <b className="text-[13px]">{name}</b>
      <audio controls preload="none" src={localMediaUrl(mediaId) ?? mediaUrl(mediaId)} className="mt-1 h-9 w-full" />
      {media?.transcript && <p className="m-0 mt-1 whitespace-pre-line text-[13px] text-[var(--muted)]">{media.transcript.replace(/^\[[\d:]+\]\s*/gm, '')}</p>}
      {media?.status === 'verarbeiten' && <p className="m-0 mt-1 text-[12px] text-[var(--muted)]">Wird transkribiert …</p>}
    </div>
  );
}

/** Globale Sprachantwort statt Tippen. */
function VoiceRecorder({ mediaId, onChange }: { mediaId: string | null; onChange: (id: string | null) => void }) {
  const { client } = useSync();
  const toast = useToast();
  const [since, setSince] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const rec = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);

  useEffect(() => {
    if (!since) return;
    const t = setInterval(() => setElapsed((Date.now() - since) / 1000), 250);
    return () => clearInterval(t);
  }, [since]);

  async function toggle() {
    if (rec.current) {
      rec.current.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const r = new MediaRecorder(stream);
      chunks.current = [];
      r.ondataavailable = (e) => chunks.current.push(e.data);
      r.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const duration = (Date.now() - (since ?? Date.now())) / 1000;
        rec.current = null;
        setSince(null);
        const mime = r.mimeType || 'audio/webm';
        const blob = new Blob(chunks.current, { type: mime });
        if (blob.size < 1000) return;
        const id = await client.queueFile({ kind: 'sprache', file: blob, fileName: `sprachantwort.${mime.includes('mp4') ? 'm4a' : 'webm'}`, mime, durationSec: duration, width: null, height: null });
        onChange(id);
      };
      rec.current = r;
      r.start();
      setSince(Date.now());
    } catch {
      toast('Kein Zugriff auf das Mikrofon');
    }
  }

  if (mediaId && !since) {
    return (
      <span className="chip">
        <Icon name="wave" size={14} />Sprachantwort bereit
        <button onClick={() => onChange(null)} aria-label="Sprachantwort verwerfen"><Icon name="x" size={12} /></button>
      </span>
    );
  }
  return (
    <button className={`chip ${since ? 'text-[var(--problem)]' : ''}`} onClick={() => void toggle()}>
      <Icon name={since ? 'stop' : 'mic'} size={14} />
      {since ? `Aufnahme ${formatSeconds(elapsed)}` : 'Lieber sprechen'}
    </button>
  );
}
